import { useMemo, useEffect } from "react";
import { Text, Button, VStack, Collapse, useDisclosure } from "@chakra-ui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { useSettings } from "utils/settingsContext";
import { useSessionJWT, decodeJWT } from "utils";
import { logInfo } from "api/accounts-customer";

/**
 * FormValidationErrorDebugView
 * @description A debug component explicitly created to help debug an issue a couple of
 * Upland users are having where the submit button is not enabled at a point in the
 * form that they should not have been able to get to without having everything being valid.
 * This only works if the integrator's acweb settings has 'debug' defined with a list of
 * allowed account IDs to show the UI to.
 * see Jira ticket: https://tiliajira.atlassian.net/browse/DEV-6087
 *
 * @example integrator acweb settings
 * {
 *    ...
 *    "debug": {
 *      "kyc_debug_allowed_account_ids": [
 *        "68a15c06-51f0-48a3-b3b9-75190bcb44e2"
 *      ]
 *    },
 *    ...
 * }
 */

const FormValidationErrorDebugView = ({ validationErrors }) => {
  const { isOpen, onToggle } = useDisclosure();
  const { integratorSettings } = useSettings();
  const { sessionJWT } = useSessionJWT();

  const userAccountIdIsAllowed = useMemo(() => {
    try {
      const decoded = decodeJWT(sessionJWT.jwt);
      const allowedAccounts = integratorSettings?.debug?.kyc_debug_allowed_account_ids || [];
      return allowedAccounts.includes(decoded?.account_id);
    } catch {
      return false;
    }
  }, [sessionJWT, integratorSettings]);

  const errors = validationErrors || {};
  const sectionCount = Object.keys(errors).length;

  useEffect(() => {
    if (isOpen)
      logInfo(sessionJWT, {
        flowLoc: "KYC_first_page",
        event: "KYC_FORM_VALIDATION_ERRORS_DEBUG",
        validationErrors,
      });
  }, [isOpen, validationErrors, sessionJWT]);

  if (userAccountIdIsAllowed) {
    return (
      <VStack align="flex-end" opacity="0.5" pb="2">
        <Button variant="ghost" size="xs" onClick={onToggle} data-testid="form-validation-debug-toggle">
          <Text as="i" color="secondaryText" fontWeight="400">
            ({sectionCount} section{sectionCount !== 1 ? "s" : ""} missing data)&nbsp;&nbsp;
          </Text>
          {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
        </Button>
        <Collapse in={isOpen}>
          <Text as="span" fontSize="xs">
            <pre>
              <code>{JSON.stringify(errors, null, 2)}</code>
            </pre>
          </Text>
        </Collapse>
      </VStack>
    );
  } else {
    return null;
  }
};

export default FormValidationErrorDebugView;
